/**
 * Kiosk Config Mapper
 * Maps minimal KioskSettingsJson to rich KioskUICustomization for preview
 * Uses defaults from device-customizer for all theme/layout values
 */

import { KioskUICustomization } from '@/features/device-customizer/kiosk/types/kiosk-types';
import { getDefaultKioskConfig } from '@/features/device-customizer/kiosk/constants/kiosk-defaults';
import { KioskSettingsJson } from '../types/kiosk-settings-types';

/**
 * Convert general settings to kiosk preview config
 * Only maps the logo field - all other values use defaults
 *
 * @param settings - Current kiosk settings from general settings
 * @param formFactor - 4inch or 5inch screen size
 * @returns Complete KioskUICustomization for preview
 */
export function mapSettingsToPreviewConfig(
  settings: KioskSettingsJson | null,
  formFactor: '4inch' | '5inch'
): KioskUICustomization {
  // Get default config for the selected form factor
  const defaultConfig = getDefaultKioskConfig(formFactor);

  // If no settings, return defaults
  if (!settings) {
    return defaultConfig;
  }

  // Map logo URL to AssetReference format
  const logoAsset = settings.logo
    ? {
        assetId: 'general-settings-logo', // Placeholder ID
        s3Url: settings.logo,
        localFileName: 'logo.png',
        contentType: 'image/png',
        size: 0,
      }
    : defaultConfig.icons.logo;

  // Return config with logo override
  return {
    ...defaultConfig,
    formFactor,
    icons: {
      ...defaultConfig.icons,
      // Use the same logo for all variants (4", 5", and default)
      logo: logoAsset,
      media4Logo: logoAsset,
      media5Logo: logoAsset,
    },
  };
}
